import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import type { PaginationQueryResponse } from '@vsolv/dev-kit/ngx';
import { debounceAfterFirst, PropertyListener } from '@vsolv/dev-kit/rx';
import { Note } from '@vsolv/packages/notes/domain';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { PaginationConfig } from '@vsolv/vectors/table';
import { SecurityService } from '@wsphere/staff/web';
import {
  BehaviorSubject,
  catchError,
  distinctUntilChanged,
  firstValueFrom,
  map,
  of,
  ReplaySubject,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs';

@Component({
  selector: 'ws-distributor-notes-page',
  templateUrl: './notes.page.html',
  styleUrls: ['./notes.page.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class DistributorNotesPage implements OnDestroy {
  private http = inject(HttpClient);
  private toastSvc = inject(ToastService);
  private security = inject(SecurityService);
  private changeDetector = inject(ChangeDetectorRef);

  @Input() distributorId: string | null = null;
  @PropertyListener('distributorId') private distributorId$ = new ReplaySubject<string | null>(1);

  protected deletingNoteId: string | null = null;

  protected searchControl = new FormControl(null);

  protected expandedNote$ = new BehaviorSubject<Note.Model | null>(null);

  protected canManageNotes$ = this.distributorId$.pipe(
    distinctUntilChanged(),
    switchMap(distributorId =>
      this.security.hasAccess('dist_CreateDistributor', distributorId ? [distributorId] : null)
    )
  );

  protected loading$ = new BehaviorSubject(true);
  readonly pagination$ = new BehaviorSubject({ limit: 10, page: 1 });

  protected pinned$ = new BehaviorSubject<Record<string, boolean>>({});

  private refresh$ = new BehaviorSubject<null>(null);

  private data$ = this.distributorId$.pipe(
    distinctUntilChanged(),
    switchMap(distributorId => this.pagination$.pipe(map(pagination => ({ ...pagination, distributorId })))),
    switchMap(params =>
      this.searchControl.valueChanges.pipe(
        startWith(this.searchControl.value),
        tap(() => {
          this.loading$.next(true);
          this.expand(null);
        }),
        debounceAfterFirst(250),
        map(search => ({ ...params, search: search ?? '' }))
      )
    ),
    switchMap(({ distributorId, ...params }) => {
      return this.refresh$.pipe(
        tap(() => {
          this.loading$.next(true);
          this.expand(null);
        }),
        switchMap(() =>
          this.http.get<PaginationQueryResponse<Note.Model>>(`/api/distributors/${distributorId}/notes`, {
            params,
          })
        )
      );
    }),
    catchError(() =>
      of(<PaginationQueryResponse<Note.Model>>{
        meta: {
          itemsPerPage: 10,

          itemCount: 0,
          currentPage: 1,

          totalItems: 0,
          totalPages: 0,
        },
        items: [],
      })
    ),
    tap(data => {
      this.pinned$.next(data.items.reduce((acc, note) => ({ ...acc, [note.id]: note.feed.includes('pinned') }), {}));
      this.loading$.next(false);
    }),
    shareReplay(1)
  );

  protected meta$ = this.data$.pipe(map(data => data?.meta));

  protected notes$ = this.data$.pipe(
    map(data => data?.items ?? []),
    switchMap(items =>
      this.pinned$.pipe(
        map(pinned =>
          items.map(item => ({
            ...item,
            pinned: !!pinned[item.id],
          }))
        )
      )
    ),
    tap(() => setTimeout(() => this.changeDetector.markForCheck()))
  );

  protected paginationConfig$ = this.meta$.pipe(
    startWith(null),
    map(meta => {
      return <PaginationConfig>{
        itemsPerPage: meta?.itemsPerPage ?? 10,
        currentPage: meta?.currentPage ?? 1,
        totalItems: meta?.totalItems ?? 0,
        pageSizes: [10, 20, 50],
      };
    })
  );

  protected trackById(_: number, item: Note.Model) {
    return item.id;
  }

  protected expand(note: Note.Model | null) {
    const current = this.expandedNote$.value;
    this.expandedNote$.next(note === null || note.id === current?.id ? null : note);

    this.changeDetector.markForCheck();
  }

  protected async togglePinned(note: Note.Model) {
    const pinned = !this.pinned$.value[note.id];
    this.pinned$.next({ ...this.pinned$.value, [note.id]: pinned });

    try {
      await firstValueFrom(this.http.post(`/api/distributors/${this.distributorId}/notes/${note.id}/pin`, { pinned }));
    } catch {
      this.pinned$.next({ ...this.pinned$.value, [note.id]: !pinned });
      this.toastSvc.show({
        type: 'error',
        title: 'Unable to pin note',
        text: 'Unable to pin note. Please try again.',
      });
    }
  }

  protected async deleteNote() {
    if (!this.deletingNoteId) return;

    try {
      this.loading$.next(true);

      await firstValueFrom(this.http.delete(`/api/distributors/${this.distributorId}/notes/${this.deletingNoteId}`));
      this.refresh();

      this.toastSvc.show({
        type: 'success',
        title: 'Note deleted',
        text: 'Note deleted and removed from the distributor.',
      });
    } catch {
      this.loading$.next(false);

      this.toastSvc.show({
        type: 'error',
        title: 'Unable to delete note',
        text: 'Unable to delete note. Please try again.',
      });
    } finally {
      this.deletingNoteId = null;
    }
  }

  ngOnDestroy() {
    this.pinned$.complete();
    this.loading$.complete();
    this.pagination$.complete();
    this.expandedNote$.complete();
    this.distributorId$.complete();
  }

  refresh() {
    this.refresh$.next(null);
  }
}
